import { verticalOf } from "./verticals.js";
export const FACES = {
    ENCARGO: { quotes: false, when: "required", workers: false, linesAtCreation: "required", party: "required", lineEdits: "replace", seated: false, cashChange: false, reportsWork: true, linesFromMenu: false, prints: false, promises: false, photos: false, sideRow: null, maintenance: false, invoices: true, booked: false, listsBy: "code", promisesDue: true },
    SERVICIO: { quotes: true, when: "optional", workers: true, linesAtCreation: "required", party: "required", lineEdits: "replace", seated: false, cashChange: false, reportsWork: true, linesFromMenu: false, prints: true, promises: true, photos: true, sideRow: "ticket", maintenance: true, invoices: true, booked: false, listsBy: "code", promisesDue: true },
    // The appointment (modules/appointments): booked before anybody knows what will be
    // done, so it may be born with no line; what was done is written on attending.
    // photos and promises stay true, each its own flag rather than read off
    // `workers`. `prints`: the cita has the sale's PDF and image.
    CITA: { quotes: false, when: "required", workers: true, linesAtCreation: "optional", party: "required", lineEdits: "replace", seated: false, cashChange: false, reportsWork: true, linesFromMenu: false, prints: true, promises: true, photos: true, sideRow: "appointment", maintenance: false, invoices: false, booked: true, listsBy: "code", promisesDue: true },
    // THE RESTAURANT (spec 2026-09-12): a table is
    // opened empty and filled plate by plate; a takeaway and a delivery are born
    // with what was ordered. All three are written by more than one waiter.
    MESA: { quotes: false, when: "now", workers: false, linesAtCreation: "optional", party: "none", lineEdits: "append", seated: true, cashChange: true, reportsWork: false, linesFromMenu: true, prints: true, promises: false, photos: false, sideRow: "table", maintenance: false, invoices: false, booked: false, listsBy: "code", promisesDue: true },
    LLEVAR: { quotes: false, when: "now", workers: false, linesAtCreation: "required", party: "optional", lineEdits: "append", seated: false, cashChange: true, reportsWork: false, linesFromMenu: true, prints: true, promises: false, photos: false, sideRow: null, maintenance: false, invoices: false, booked: false, listsBy: "code", promisesDue: true },
    DELIVERY: { quotes: false, when: "required", workers: false, linesAtCreation: "required", party: "required", lineEdits: "append", seated: false, cashChange: true, reportsWork: false, linesFromMenu: true, prints: true, promises: false, photos: false, sideRow: null, maintenance: false, invoices: false, booked: false, listsBy: "code", promisesDue: true },
    // THE CAR WASH (spec 2026-10-03 «El lavado se reescribe» §D.2): a sale, born
    // done and charged from «Nueva venta», with who washed it. No quote, no
    // warranty, no maintenance, nothing to report: the ticket would answer four
    // of these wrong. `sideRow: "ticket"` keeps the account's due day when it is
    // left owing; `promises: false` leaves its warranty null.
    LAVADO: { quotes: false, when: "optional", workers: true, linesAtCreation: "required", party: "required", lineEdits: "replace", seated: false, cashChange: false, reportsWork: false, linesFromMenu: false, prints: true, promises: false, photos: false, sideRow: "ticket", maintenance: false, invoices: true, booked: false, listsBy: "object", promisesDue: false },
};
/**
 * HOW A BUSINESS'S LIST READS ITS DOCUMENTS, from the faces it writes: by
 * their object only when every face reads its own that way, and with
 * «Atrasados» and «Entrega» while any face promises a day.
 */
export function listShapeOf(faces) {
    return {
        byObject: faces.length > 0 && faces.every((face) => FACES[face].listsBy === "object"),
        promisesDue: faces.some((face) => FACES[face].promisesDue),
    };
}
/** The keys of `FACES`, in their order: what a snapshot or a loop reaches for instead of naming a face. */
export const DOCUMENT_FACES = Object.keys(FACES);
export const FACE_WORDS = {
    ENCARGO: { paperTitle: "Encargo", paperFeminine: false, partyMissing: "Escribe para quién es." },
    SERVICIO: { paperTitle: "Servicio", paperFeminine: false, partyMissing: "Elige el cliente." },
    CITA: { paperTitle: "Cita", paperFeminine: true, partyMissing: "Elige a quién." },
    MESA: { paperTitle: "Cuenta", paperFeminine: true, partyMissing: "Elige el cliente." },
    LLEVAR: { paperTitle: "Para llevar", paperFeminine: false, partyMissing: "Elige el cliente." },
    DELIVERY: { paperTitle: "Delivery", paperFeminine: false, partyMissing: "Elige el cliente." },
    LAVADO: { paperTitle: "Lavado", paperFeminine: false, partyMissing: "Elige el cliente." },
};
/** How each face reads on a screen: the restaurant's «+» offers «Llevar» and «Delivery». No screen spells a face name. */
export const FACE_LABELS = {
    ENCARGO: "Encargo",
    SERVICIO: "Servicio",
    CITA: "Cita",
    MESA: "Mesa",
    LLEVAR: "Llevar",
    DELIVERY: "Delivery",
    LAVADO: "Lavado",
};
/**
 * The faces THIS TYPE CAN write, in tab-bar order: its record's. It decides by
 * type and nothing else: whether a face is also OFFERED may depend on data (the
 * restaurant's table, only with active tables), and that filter belongs to the
 * module that owns the data, never here.
 */
export function facesFor(type) {
    return [...verticalOf(type).faces];
}
/** Whether this type seats customers at tables: Configuración's «Mesas», the onboarding's question. */
export function seatsTables(type) {
    return facesFor(type).some((face) => FACES[face].seated);
}
/** Whether any face of this type is quoted: the Cotizaciones screen, the «Solo» answer, the quote words. */
export function offersQuotes(type) {
    return facesFor(type).some((face) => FACES[face].quotes);
}
/** Whether this trade hands its work to named people: its record's answer (the workshop, the car wash). */
export function assignsWork(type) {
    return verticalOf(type).assignsWork;
}
/** «¿Quién lo hace?» on a document of this face in this trade: the face has workers and the trade assigns work. */
export function assignsWorkOn(face, type) {
    return FACES[face].workers && assignsWork(type);
}
/**
 * Whether Configuración's «Plazo de pago (días)» reaches anything here: the
 * term is copied only onto a face with the ticket's side row (`writeServiceRow`).
 */
export function copiesPaymentTerm(type) {
    return facesFor(type).some((face) => FACES[face].sideRow === "ticket");
}
/** Whether its «Garantía por defecto» does: the same row, and only where the face promises. */
export function copiesWarranty(type) {
    return facesFor(type).some((face) => FACES[face].sideRow === "ticket" && FACES[face].promises);
}
/** The face the list's «+» creates: the first one that does not sit at a table (a table opens from the strip). */
export function workFace(type) {
    return facesFor(type).find((face) => !FACES[face].seated) ?? "ENCARGO";
}
/** What THIS document is, read off its own frozen face -- never off the business type. */
export function faceSpecOf(doc) {
    return FACES[doc.serviceType];
}
